import { Grid, Skeleton } from '@mui/material';
import { Formik, FormikErrors } from 'formik';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import {
	CerebroBreadcrumb,
	CerebroForm,
	CerebroPageContainer,
	CerebroPrimaryButton,
	CerebroRichText,
	CerebroSummary,
} from '../shared/components';
import { useApi } from '../shared/context';
import { QueryKeys, useDealRequestAllocationQuery } from '../shared/hooks';
import { DealRequestAllocation } from '../shared/models';
import { FormikUtils } from '../shared/utils';
import { CerebroApiEndpoints } from '../shared/utils/api';
import { getApiResponseData } from '../shared/utils/axios';

export const Notes: React.FC = () => {
	const api = useApi();
	const { guid } = useParams<'guid'>();
	const allocationQuery = useDealRequestAllocationQuery(guid);

	const client = useQueryClient();
	const snackbar = useSnackbar();

	const notesMutation = useMutation(
		async ({ values }: NotesMutationVariables) => {
			const { data, error } = await getApiResponseData<DealRequestAllocation>(
				api.patch(CerebroApiEndpoints.Allocation(guid), {
					...values,
				})
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: (data, { helpers }) => {
				client.setQueryData(QueryKeys.Allocation(guid), data);
				snackbar.enqueueSnackbar('Progress Saved.', { variant: 'success' });
				helpers.resetForm({
					values: { loanRequestNotes: data?.loanRequestNotes ?? '' },
				});
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroPageContainer>
			<CerebroBreadcrumb>Employee Notes</CerebroBreadcrumb>

			{allocationQuery.isLoading ? (
				<Skeleton height={400} />
			) : (
				<Formik
					initialValues={{
						loanRequestNotes: allocationQuery.data?.loanRequestNotes ?? '',
					}}
					onSubmit={FormikUtils.MutateOnSubmit(notesMutation.mutate)}
				>
					{({ isSubmitting, setFieldValue, handleSubmit, values }) => {
						return (
							<CerebroForm onSubmit={handleSubmit}>
								<CerebroRichText
									value={values.loanRequestNotes ?? ''}
									placeholder="Customer needs, in their own words."
									onChange={updatedValue => setFieldValue('loanRequestNotes', updatedValue)}
								/>

								<Grid
									container
									sx={{ paddingBottom: 4 }}
									justifyContent={{ xs: 'center', md: 'flex-start' }}
								>
									<Grid item>
										<CerebroPrimaryButton loading={isSubmitting} type="submit">
											Save
										</CerebroPrimaryButton>
									</Grid>
								</Grid>

								<CerebroSummary showLinks={true} compressView={true} />
							</CerebroForm>
						);
					}}
				</Formik>
			)}
		</CerebroPageContainer>
	);
};

type NotesMutationVariables = FormikUtils.MutateOnSubmitVariables<NotesFormValues>;
interface NotesFormValues {
	loanRequestNotes: string;
}
type NotesFormValueErrors = FormikErrors<NotesFormValues>;
